import Ionicons from "@expo/vector-icons/Ionicons";
import { type ComponentProps, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from "react-native";

import { Modal } from "@/components/ui/app-modal";

import { reflectionsApi } from "@/api";
import { ThemedText } from "@/components/themed-text";
import { BlurredBackdrop } from "@/components/ui/blurred-backdrop";
import { Radius, Spacing } from "@/constants/theme";
import { useTheme } from "@/hooks/use-theme";
import i18n from "@/i18n";
import { confirm, notify } from "@/lib/dialog";
import {
  reflectionDateTime,
  reflectionFeelingLabel,
  reflectionOutcomeLabel,
  reflectionSubjectLabel,
  reflectionWhereLabel,
} from "@/lib/reflection-format";
import type { Reflection } from "@/types";

/**
 * View of a single reflection the user tapped in the progress list. Renders a
 * reflection already loaded on the client — no fetch required.
 *
 * Deleting from here is the only thing in the app that removes a reflection;
 * nothing expires on a schedule. That makes it worth a confirmation, since
 * there is no undo and no copy left behind.
 */
export function ReflectionDetailModal({
  reflection,
  onClose,
  onDelete,
}: {
  reflection: Reflection | null;
  onClose: () => void;
  /** Omit to render read-only. Called after the delete succeeds. */
  onDelete?: (id: string) => void;
}) {
  useTranslation();
  const c = useTheme();
  const [deleting, setDeleting] = useState(false);
  const detailTones = useMemo(
    () => detailToneRotation(reflection?.id),
    [reflection?.id],
  );

  async function handleDelete(id: string) {
    // Close before asking, and before the failure notice below: both go
    // through the shared ConfirmDialog, which is a native Modal, and iOS will
    // not present one over this Modal — the dialog never appears and its
    // invisible window eats every touch until the screen unmounts.
    onClose();
    const ok = await confirm({
      title: i18n.t("growth.detail.deleteTitle"),
      message: i18n.t("growth.detail.deleteBody"),
      confirmLabel: i18n.t("growth.detail.deleteConfirm"),
      cancelLabel: i18n.t("growth.detail.deleteCancel"),
      destructive: true,
    });
    if (!ok) return;
    setDeleting(true);
    try {
      await reflectionsApi.remove(id);
      onDelete?.(id);
    } catch {
      notify(i18n.t("growth.detail.deleteFailed"));
    } finally {
      setDeleting(false);
    }
  }

  const feeling = reflection?.feeling
    ? reflectionFeelingLabel(reflection.feeling)
    : undefined;
  const outcome = reflection?.outcome
    ? reflectionOutcomeLabel(reflection.outcome)
    : undefined;
  const subject = reflection?.subject
    ? reflectionSubjectLabel(reflection.subject)
    : undefined;
  const where = reflection?.where
    ? reflectionWhereLabel(reflection.where)
    : undefined;
  const title = outcome ?? subject ?? i18n.t("growth.reflection");
  const note = reflection?.note?.trim();

  return (
    <Modal
      visible={reflection !== null}
      transparent
      animationType="fade"
      statusBarTranslucent
      onRequestClose={onClose}
    >
      <View style={styles.screen}>
        <BlurredBackdrop
          accessibilityLabel={i18n.t("growth.detail.closeA11y")}
          style={styles.backdrop}
          onPress={onClose}
        />

        <View style={styles.sheetWrap}>
          <View
            style={[
              styles.sheet,
              { backgroundColor: c.surface, borderColor: c.border },
            ]}
          >
            {reflection ? (
              <>
                <View style={styles.header}>
                  <View style={styles.headerCopy}>
                    <View style={styles.eyebrowRow}>
                      <Ionicons
                        name="sparkles-outline"
                        size={22}
                        color={c.tintStrong}
                      />
                      <ThemedText
                        variant="bodyStrong"
                        tone="tint"
                        style={styles.eyebrow}
                      >
                        {i18n.t("growth.detail.title")}
                      </ThemedText>
                    </View>
                    <ThemedText variant="body" tone="muted">
                      {reflectionDateTime(reflection.createdAt)}
                    </ThemedText>
                  </View>
                  <Pressable
                    onPress={onClose}
                    hitSlop={10}
                    accessibilityLabel={i18n.t("common.close")}
                    style={[
                      styles.closeButton,
                      { backgroundColor: c.surfaceAlt },
                    ]}
                  >
                    <Ionicons name="close" size={26} color={c.textSecondary} />
                  </Pressable>
                </View>

                <ThemedText variant="title" style={styles.title}>
                  {title}
                </ThemedText>

                <ScrollView
                  style={styles.body}
                  showsVerticalScrollIndicator={false}
                >
                  <View style={styles.detailList}>
                    {where ? (
                      <DetailRow
                        icon="location-outline"
                        iconTone={detailTones.where}
                        label={i18n.t("growth.detail.where")}
                        value={where}
                        pillTone={detailTones.where}
                        c={c}
                      />
                    ) : null}
                    {subject ? (
                      <DetailRow
                        icon="chatbubble-outline"
                        iconTone={detailTones.subject}
                        label={i18n.t("growth.detail.whatHappened")}
                        value={subject}
                        pillTone={detailTones.subject}
                        c={c}
                      />
                    ) : null}
                    {feeling ? (
                      <DetailRow
                        icon="happy-outline"
                        iconTone={detailTones.feeling}
                        label={i18n.t("growth.detail.feeling")}
                        value={feeling}
                        pillTone={detailTones.feeling}
                        c={c}
                      />
                    ) : null}
                    {outcome ? (
                      <DetailRow
                        icon="flag-outline"
                        iconTone={detailTones.outcome}
                        label={i18n.t("growth.detail.outcome")}
                        value={outcome}
                        pillTone={detailTones.outcome}
                        c={c}
                      />
                    ) : null}
                    <DetailRow
                      icon="document-text-outline"
                      iconTone="neutral"
                      label={i18n.t("growth.detail.note")}
                      value={note || i18n.t("growth.detail.noNote")}
                      muted={!note}
                      c={c}
                    />
                  </View>

                  <View
                    style={[
                      styles.reassurance,
                      {
                        backgroundColor: c.surfaceAlt,
                        borderColor: c.border,
                      },
                    ]}
                  >
                    <View
                      style={[
                        styles.reassuranceIcon,
                        { backgroundColor: c.backgroundSelected },
                      ]}
                    >
                      <Ionicons
                        name="heart-outline"
                        size={28}
                        color={c.tintStrong}
                      />
                    </View>
                    <View style={styles.reassuranceCopy}>
                      <ThemedText
                        variant="body"
                        tone="secondary"
                        style={styles.reassuranceText}
                      >
                        {i18n.t("growth.detail.encouragement")}
                      </ThemedText>
                      <ThemedText
                        variant="bodyStrong"
                        tone="tint"
                        style={styles.reassuranceText}
                      >
                        {i18n.t("growth.detail.counts")}
                      </ThemedText>
                    </View>
                  </View>

                  {onDelete ? (
                    <Pressable
                      accessibilityRole="button"
                      disabled={deleting}
                      onPress={() => void handleDelete(reflection.id)}
                      style={[
                        styles.deleteButton,
                        { borderColor: c.border },
                        deleting && styles.deleteButtonBusy,
                      ]}
                    >
                      {deleting ? (
                        <ActivityIndicator color={c.danger} />
                      ) : (
                        <>
                          <Ionicons
                            name="trash-outline"
                            size={17}
                            color={c.danger}
                          />
                          <ThemedText
                            variant="bodyStrong"
                            style={{ color: c.danger }}
                          >
                            {i18n.t("growth.detail.delete")}
                          </ThemedText>
                        </>
                      )}
                    </Pressable>
                  ) : null}
                </ScrollView>
              </>
            ) : null}
          </View>
        </View>
      </View>
    </Modal>
  );
}

function DetailRow({
  icon,
  iconTone,
  label,
  value,
  pillTone,
  muted,
  c,
}: {
  icon: ComponentProps<typeof Ionicons>["name"];
  iconTone: IconTone;
  label: string;
  value: string;
  pillTone?: DetailTone;
  muted?: boolean;
  c: ReturnType<typeof useTheme>;
}) {
  const tone = DETAIL_TONES[iconTone];

  return (
    <View style={[styles.detailRow, { borderColor: c.border }]}>
      <View style={[styles.detailIcon, { backgroundColor: tone.background }]}>
        <Ionicons name={icon} size={26} color={tone.foreground} />
      </View>

      <View style={styles.detailCopy}>
        <ThemedText variant="label" tone="muted" style={styles.detailLabel}>
          {label}
        </ThemedText>
        {pillTone ? (
          <ValuePill value={value} tone={pillTone} />
        ) : (
          <ThemedText
            variant="bodyStrong"
            tone={muted ? "muted" : "default"}
            style={styles.detailValue}
          >
            {value}
          </ThemedText>
        )}
      </View>
    </View>
  );
}

function ValuePill({
  value,
  tone,
}: {
  value: string;
  tone: DetailTone;
}) {
  const colors = VALUE_PILL_TONES[tone];

  return (
    <View
      style={[styles.valuePill, { backgroundColor: colors.backgroundColor }]}
    >
      <ThemedText
        variant="bodyStrong"
        style={[styles.valuePillText, { color: colors.color }]}
      >
        {value}
      </ThemedText>
    </View>
  );
}

const DETAIL_TONE_ORDER = ["purple", "cyan", "amber", "green"] as const;

type DetailTone = (typeof DETAIL_TONE_ORDER)[number];
type IconTone = DetailTone | "neutral";

function detailToneRotation(seed?: string) {
  let offset = 0;
  for (const char of seed ?? "") {
    offset = (offset + char.charCodeAt(0)) % DETAIL_TONE_ORDER.length;
  }
  const toneAt = (index: number) =>
    DETAIL_TONE_ORDER[(index + offset) % DETAIL_TONE_ORDER.length];
  return {
    where: toneAt(0),
    subject: toneAt(1),
    feeling: toneAt(2),
    outcome: toneAt(3),
  };
}

const DETAIL_TONES: Record<IconTone, { background: string; foreground: string }> = {
  purple: { background: "rgba(194, 118, 255, 0.16)", foreground: "#C276FF" },
  cyan: { background: "rgba(88, 200, 231, 0.16)", foreground: "#58C8E7" },
  amber: { background: "rgba(255, 214, 128, 0.15)", foreground: "#FFE09A" },
  green: { background: "rgba(141, 237, 173, 0.14)", foreground: "#8DEDAE" },
  neutral: { background: "rgba(148, 163, 184, 0.13)", foreground: "#94A3B8" },
};

const VALUE_PILL_TONES: Record<
  DetailTone,
  { backgroundColor: string; color: string }
> = {
  purple: { backgroundColor: "#C276FF", color: "#FFFFFF" },
  cyan: { backgroundColor: "#58C8E7", color: "#FFFFFF" },
  amber: { backgroundColor: "#FFE09A", color: "#4F3A12" },
  green: { backgroundColor: "#AEEFC1", color: "#123B24" },
};

const styles = StyleSheet.create({
  screen: { flex: 1 },
  backdrop: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(8, 15, 26, 0.5)",
  },
  sheetWrap: {
    flex: 1,
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: Spacing.lg,
    paddingVertical: 28,
  },
  sheet: {
    width: "100%",
    maxWidth: 420,
    maxHeight: "82%",
    padding: Spacing.xl,
    gap: Spacing.lg,
    borderRadius: Radius.xl,
    borderWidth: 1,
    shadowColor: "#000000",
    shadowOpacity: 0.24,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: 12 },
    elevation: 12,
  },
  header: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: Spacing.md,
  },
  headerCopy: {
    flex: 1,
    gap: 4,
  },
  eyebrowRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  eyebrow: {
    fontSize: 20,
    lineHeight: 25,
    letterSpacing: 0,
  },
  closeButton: {
    width: 54,
    height: 54,
    borderRadius: 27,
    alignItems: "center",
    justifyContent: "center",
  },
  title: {
    fontSize: 34,
    lineHeight: 41,
    letterSpacing: 0,
  },
  body: {
    flexShrink: 1,
    maxHeight: 430,
  },
  detailList: {
    marginBottom: Spacing.lg,
  },
  detailRow: {
    minHeight: 88,
    paddingVertical: Spacing.md,
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.md,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  detailIcon: {
    width: 64,
    height: 64,
    borderRadius: 16,
    alignItems: "center",
    justifyContent: "center",
  },
  detailCopy: {
    flex: 1,
    gap: 6,
  },
  detailLabel: {
    fontSize: 15,
    lineHeight: 20,
    letterSpacing: 0,
  },
  detailValue: {
    fontSize: 18,
    lineHeight: 24,
  },
  valuePill: {
    alignSelf: "flex-start",
    maxWidth: "100%",
    minHeight: 38,
    paddingHorizontal: 18,
    paddingVertical: 8,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: Radius.pill,
  },
  valuePillText: {
    fontSize: 15,
    lineHeight: 20,
  },
  deleteButton: {
    marginTop: Spacing.md,
    minHeight: 46,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: Spacing.sm,
    borderRadius: Radius.lg,
    borderWidth: StyleSheet.hairlineWidth,
  },
  deleteButtonBusy: { opacity: 0.6 },
  reassurance: {
    padding: Spacing.md,
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.md,
    borderRadius: Radius.lg,
    borderWidth: 1,
  },
  reassuranceIcon: {
    width: 54,
    height: 54,
    borderRadius: 16,
    alignItems: "center",
    justifyContent: "center",
  },
  reassuranceCopy: {
    flex: 1,
  },
  reassuranceText: {
    fontSize: 14,
    lineHeight: 18,
  },
});
